/**
 * The frontend half of the entity-link registry — one entry per bulk-editable
 * relationship, keyed exactly as the backend registers it
 * (`shared/services/entity_link_registry.py`).
 *
 * A list page asks this map what relationships its OWN entity type can be the
 * selection side of, and renders the answer. That is what makes "select twenty
 * contacts and put them all on this deal" a registry entry rather than a
 * feature: master_data's contact list offers a pipeline action without importing
 * pipeline, and adding the next relationship is one row here plus one provider
 * there.
 *
 * **`Picker` is `lazy(() => import(...))`. Always.** A `src/shared/` re-export
 * shim is bundler-transparent — the same static edge to Rollup — and Vite's
 * `manualChunks` splits vendor packages only, so app-code chunking is entirely
 * emergent from `import()` boundaries. A static import here would pull every
 * contributing module's picker, its hooks and its API client into the chunk of
 * every list page that offers a bulk link. The label, the icon and the module id
 * all render BEFORE the picker mounts — that is why only `Picker` is lazy.
 *
 * See `docs/platform/entity-relationship-surfaces-design.md` §4.2 and §6.4.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { Handshake, type LucideIcon } from 'lucide-react';

/**
 * What a contributed picker is handed. Deliberately narrow: the bar owns the
 * open/closed state, the registry entry owns the picker, and the only thing that
 * crosses between them is the id the user chose.
 */
export interface BulkLinkPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The chosen target. The picker closes itself. */
  onConfirm: (targetId: string) => void;
  /** Confirm button label, already translated by the shared action. */
  confirmLabel: string;
}

export interface BulkLinkActionDefinition {
  /** Registry key, byte-identical to the backend's. */
  key: string;
  /** Contributing module id — the action is dropped when it is not accessible. */
  moduleId: string;
  /** Canonical `module.entity` key of the MULTI-SELECTED side. */
  selectionEntityType: string;
  /** Namespaced i18n key for the action label. */
  labelI18nKey: string;
  /** Namespaced i18n key for the unlink variant's label. */
  unlinkLabelI18nKey: string;
  /**
   * Namespaced i18n keys for the picker's confirm button, ONE PER OPERATION.
   *
   * A single shared key made the unlink picker's confirm read "Add to this
   * Deal" — the opposite verb on the destructive half, and the only thing on
   * screen naming what the button does once the dialog is open. Caught in
   * browser validation, which is the only place it is visible: the two actions
   * share one picker component, so nothing in the types or the tests disagreed.
   */
  confirmLinkLabelI18nKey: string;
  confirmUnlinkLabelI18nKey: string;
  icon: LucideIcon;
  Picker: LazyExoticComponent<ComponentType<BulkLinkPickerProps>>;
}

export const BULK_LINK_ACTIONS: BulkLinkActionDefinition[] = [
  {
    key: 'pipeline.deal_contacts',
    moduleId: 'pipeline',
    selectionEntityType: 'master_data.contact',
    labelI18nKey: 'pipeline:entityLinks.dealContacts.link',
    unlinkLabelI18nKey: 'pipeline:entityLinks.dealContacts.unlink',
    confirmLinkLabelI18nKey: 'pipeline:entityLinks.dealContacts.confirmLink',
    confirmUnlinkLabelI18nKey: 'pipeline:entityLinks.dealContacts.confirmUnlink',
    icon: Handshake,
    Picker: lazy(async () => {
      const mod = await import('../../modules/pipeline/components/BulkDealContactsPicker');
      return { default: mod.BulkDealContactsPicker };
    }),
  },
];

/** The relationships a list of `selectionEntityType` can offer as a bulk action. */
export function bulkLinkActionsFor(selectionEntityType: string): BulkLinkActionDefinition[] {
  return BULK_LINK_ACTIONS.filter((a) => a.selectionEntityType === selectionEntityType);
}
